import { create } from 'zustand'
import { useDiagramStore } from './diagramStore'
import { useHistoryStore, type DiagramSnapshot } from './historyStore'

function uniqueHiddenTableIds(hiddenTableIds: string[]): string[] {
  return [...new Set(hiddenTableIds)]
}

function buildHistorySnapshot(hiddenTableIds: string[]): DiagramSnapshot {
  const diagram = useDiagramStore.getState()
  return {
    tables: diagram.tables,
    rels: diagram.rels,
    groups: diagram.groups,
    defaults: diagram.defaults,
    projectName: diagram.projectName,
    dialect: diagram.dialect,
    hiddenTableIds,
  }
}

function normalizeSnapshotForComparison(snapshot: DiagramSnapshot): DiagramSnapshot {
  return {
    ...snapshot,
    hiddenTableIds: uniqueHiddenTableIds(snapshot.hiddenTableIds ?? []),
  }
}

/**
 * Visibility is stored outside diagramStore, but shares its undo/redo stack.
 * Ensure the current combined state is the stack baseline before changing it.
 * This is especially important immediately after loading old tabs/projects,
 * whose history can be empty or can predate hiddenTableIds in snapshots.
 */
function ensureHistoryBaseline(hiddenTableIds: string[]): void {
  const history = useHistoryStore.getState()
  const currentSnapshot = buildHistorySnapshot(hiddenTableIds)
  const lastSnapshot = history.undoStack.at(-1)?.snapshot

  if (!lastSnapshot) {
    history.initHistory(currentSnapshot)
    return
  }

  if (
    JSON.stringify(normalizeSnapshotForComparison(lastSnapshot)) !==
    JSON.stringify(normalizeSnapshotForComparison(currentSnapshot))
  ) {
    history.commitHistory(currentSnapshot, 'sync-before-table-visibility')
  }
}

interface TabViewState {
  hiddenTableIds: string[]
  setHiddenTableIds: (hiddenTableIds: string[]) => void
  hideTables: (tableIds: string[]) => void
  showTables: (tableIds: string[]) => void
  toggleTableVisibility: (tableId: string) => void
  isTableHidden: (tableId: string) => boolean
  clearHiddenTables: () => void
}

export const useTabViewStore = create<TabViewState>()((set, get) => ({
  hiddenTableIds: [],

  setHiddenTableIds: (hiddenTableIds) => {
    set({ hiddenTableIds: uniqueHiddenTableIds(hiddenTableIds) })
  },

  hideTables: (tableIds) => {
    const currentHiddenTableIds = get().hiddenTableIds
    const nextHiddenTableIds = uniqueHiddenTableIds([
      ...currentHiddenTableIds,
      ...tableIds,
    ])

    if (nextHiddenTableIds.length === currentHiddenTableIds.length) return

    ensureHistoryBaseline(currentHiddenTableIds)
    set({ hiddenTableIds: nextHiddenTableIds })
    useHistoryStore.getState().commitHistory(
      buildHistorySnapshot(nextHiddenTableIds),
      tableIds.length > 1 ? 'hide-tables' : 'hide-table',
    )
  },

  showTables: (tableIds) => {
    const currentHiddenTableIds = get().hiddenTableIds
    const tableIdSet = new Set(tableIds)
    const nextHiddenTableIds = currentHiddenTableIds.filter((tableId) => !tableIdSet.has(tableId))

    if (nextHiddenTableIds.length === currentHiddenTableIds.length) return

    ensureHistoryBaseline(currentHiddenTableIds)
    set({ hiddenTableIds: nextHiddenTableIds })
    useHistoryStore.getState().commitHistory(
      buildHistorySnapshot(nextHiddenTableIds),
      tableIds.length > 1 ? 'show-tables' : 'show-table',
    )
  },

  toggleTableVisibility: (tableId) => {
    if (get().hiddenTableIds.includes(tableId)) {
      get().showTables([tableId])
      return
    }
    get().hideTables([tableId])
  },

  isTableHidden: (tableId) => get().hiddenTableIds.includes(tableId),

  clearHiddenTables: () => {
    const currentHiddenTableIds = get().hiddenTableIds
    if (currentHiddenTableIds.length === 0) return
    ensureHistoryBaseline(currentHiddenTableIds)
    set({ hiddenTableIds: [] })
    useHistoryStore.getState().commitHistory(buildHistorySnapshot([]), 'clear-hidden-tables')
  },
}))
